import { CreativeOSFeatures, CreativeOSPages } from '@/_shared/subscriptionPlans'
import AccessControl from '@/components/AccessControl'
import { FixesItem, ScoreMeasures, UploadAdImage } from '@/components/FCA'
import { Stack } from '@/components/custom-components'
import { Loader } from '@/components/custom-components/Loader'
import { LockTagSmall } from '@/components/custom-components/LockTag'
// import { LockTagSmall } from '@/components/custom-components/LockTag';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb.tsx'
import { Button } from '@/components/shadcn/button.tsx'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/shadcn/form.tsx'
import { Input } from '@/components/shadcn/input'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import {
  UnderlineTabs,
  UnderlineTabsContent,
  UnderlineTabsList,
  UnderlineTabsTrigger,
} from '@/components/shadcn/underline-tabs'
import { useElementSize } from '@/hooks/useElementSize'
import showToastNotification from '@/hooks/useShowToast'
import useUserPermissions from '@/hooks/useUserPermissions'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { trpc } from '@/utils/trpc'
import { zodResolver } from '@hookform/resolvers/zod'
import { Download, Image, Share } from '@mynaui/icons-react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import SemiCircleProgressBar from 'react-progressbar-semicircle'
import { z } from 'zod'

type SearchParams = {
  sideBarOpen?: boolean
}

type ScoreItem = {
  score: number
  description: string
}

type ScoreBreakdown = Record<string, ScoreItem>

type CoherenceEvaluation = {
  overall_score: {
    score: number
    grade: string
    emotional_label: string
    visceral_description: string
  }
  score_breakdown: ScoreBreakdown
}

type KeyOpportunity = {
  heading: string
  description: string
}

type EvaluationSummary = {
  score: number
  key_opportunities: KeyOpportunity[]
}

type Analysis = {
  overview: string
  whatsWorkingWell: string[] | string
  areasForImprovement: string[] | string
}

type FunnelCongruenceAnalysis = {
  coherence_evaluation: CoherenceEvaluation
  opportunity_assessment: EvaluationSummary
  analysis: Analysis
}

export type FunnelCongruenceAnalysisResponse = {
  ad_images_data: string[]
  landing_page_image_data: string
  analysis: FunnelCongruenceAnalysis
}

export const Route = createFileRoute('/feeds/creative-toolkit/creative-analysis/')({
  component: All,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const sideBarOpen = search?.sideBarOpen as boolean

    return {
      sideBarOpen,
    }
  },
})

function All() {
  const [squareRef, { width = 0 }] = useElementSize()
  const { authUser } = useUserPermissions()

  const [uploadStep, setUploadStep] = useState<number>(1)
  const [landingPageImage, setLandingPageImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [adImageUrl, setAdImageUrl] = useState<string | undefined>(undefined)
  const [lastCapturedUrl, setLastCapturedUrl] = useState<string>('')
  const [funnelCongruenceAnalysis, setFunnelCongruenceAnalysis] = useState<FunnelCongruenceAnalysisResponse | null>(
    null,
  )

  const fcaFeatureFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.fca)

  const navigate = useNavigate()

  useEffect(() => {
    if (fcaFeatureFlagEnabled === false) {
      navigate({ to: '/feeds/templates' })
      return
    }
  }, [fcaFeatureFlagEnabled])

  const formSchema = z.object({
    target_audience: z.string().optional(),
    channel: z.string().optional(),
    offer: z.string().optional(),
    adImage: z.instanceof(File).optional(),
    landingPageUrl: z.string().url('Please enter a valid URL'),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      target_audience: '',
      channel: '',
      offer: '',
      adImage: undefined,
      landingPageUrl: '',
    },
  })

  const { mutate: uploadLandingPageMutation } = trpc.uploadAsUrl.useMutation({
    onSuccess: (data) => {
      setLandingPageImage(data)
      setIsUploading(false)
    },
    onError: () => {
      showToastNotification('error', {
        message: 'Error',
        description: 'An error occurred. Please try again.',
      })
      setIsUploading(false)
    },
  })
  const utils = trpc.useUtils()

  const { mutate: createFunnelCongruenceAnalysis, isPending: isCreatingFunnelCongruenceAnalysis } =
    trpc.createFunnelCongruenceAnalysis.useMutation({
      onSuccess: (data) => {
        if (data && 'ad_images_data' in data && 'landing_page_image_data' in data) {
          setFunnelCongruenceAnalysis(data as FunnelCongruenceAnalysisResponse)
          showToastNotification('success', {
            message: 'Funnel congruence analysis generated successfully',
          })
          utils.getUserLimits.invalidate()
          setTimeout(() => {
            setUploadStep(2)
          }, 2000)
        }
      },
      onError: () => {
        showToastNotification('error', {
          message: 'Error',
          description: 'An error occurred. Please try again.',
        })
      },
    })

  const handleSubmitFunnelCongruenceAnalysis = async (values: z.infer<typeof formSchema>) => {
    try {
      if (adImageUrl && landingPageImage) {
        createFunnelCongruenceAnalysis({
          target_audience: values.target_audience,
          channel: values.channel,
          offer: values.offer,
          images: [
            {
              url: adImageUrl,
              type: 'ad',
            },
            {
              url: landingPageImage,
              type: 'landing-page',
            },
          ],
          landingPageUrl: values.landingPageUrl,
          name: authUser?.firstName + ' ' + authUser?.lastName,
          email: authUser?.email,
        })
      }
    } catch (error) {
      console.error('Error', error)
    }
  }

  const handleSubmitUploadAdImage = async (values: z.infer<typeof formSchema>) => {
    setIsUploading(true)
    try {
      if (!values.adImage) {
        throw new Error('Ad image is required')
      }

      // Upload ad image via axios
      const formData = new FormData()
      formData.append('images', values.adImage)

      const imageUploadResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}funnel-congruence/image-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )

      // Store the ad image URL
      if (imageUploadResponse && imageUploadResponse.data.imageUrls?.[0]) {
        const uploadedUrl = imageUploadResponse.data.imageUrls[0]
        setAdImageUrl(uploadedUrl)

        // Always regenerate landing page when ad image changes
        uploadLandingPageMutation({
          landing_page_url: values.landingPageUrl,
        })
        setLastCapturedUrl(values.landingPageUrl)
      }
    } catch (e) {
      console.error('Error', e)
      setIsUploading(false)
    }
  }

  return (
    <AccessControl page={CreativeOSPages.CREATIVE_TOOLKIT_FCA}>
      <Stack ref={squareRef} className="gap-3 lg:gap-6 pb-5">
        <Stack className="gap-3 lg:gap-8">
          <Stack className="gap-3 lg:gap-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={'/feeds/creative-toolkit'}>Toolkit</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Funnel Congruence Analysis</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className={'flex justify-between gap-5 items-center'}>
              <div className={''}>
                <h1 className={'text-lg lg:text-2xl font-semibold'}>Upload Your Ad and Capture Your Landing Page.</h1>
                <p className={'text-lg lg:text-base text-thememutedforeground mt-2'}>
                  See how well your ad and landing page work together with a visual analysis.
                </p>
              </div>
              {uploadStep === 3 ? (
                <div className="flex gap-4">
                  <Button size={'sm'} variant={'secondary'}>
                    <Download size={16} /> <span>Export</span>
                  </Button>
                  <Button size={'sm'} variant={'secondary'}>
                    <Share size={16} /> <span>Share & Collaborate</span>
                  </Button>
                </div>
              ) : null}
            </div>
          </Stack>
        </Stack>
        <div className="flex flex-col gap-3">
          <Form {...form}>
            {uploadStep === 1 ? (
              <div>
                {isCreatingFunnelCongruenceAnalysis ? (
                  <div className="flex flex-col justify-center items-center w-full h-[60vh] min-h-[60vh] max-h-[60vh]">
                    <Loader />
                    <p className="text-sm text-themeforeground mt-3">Scoring Funnel Congruence...</p>
                  </div>
                ) : (
                  <form onSubmit={form.handleSubmit(handleSubmitUploadAdImage)}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <h3>Upload Ad Image</h3>
                        <div>
                          <UploadAdImage
                            onFileSelect={(file) => {
                              form.setValue('adImage', file)
                            }}
                            previewUrl={adImageUrl}
                            disabled={isUploading}
                            onRemove={() => {
                              setAdImageUrl(undefined)
                              form.setValue('adImage', undefined)
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        {isUploading ? (
                          <div className="flex flex-col justify-center items-center w-full h-[50svh] lg:h-[65svh] lg:min-h-[65svh] lg:max-h-[65svh]">
                            <Loader />
                            <p className="text-sm text-themeforeground mt-3">Generating Screenshot...</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <h3>Landing Page URL</h3>
                            <FormField
                              control={form.control}
                              name="landingPageUrl"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      className="shadow-none"
                                      placeholder="e.g, https://creativeos.com"
                                      {...field}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                        }
                                      }}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            {landingPageImage ? (
                              <div className="mt-4">
                                <ScrollArea className="h-[54svh]">
                                  <img
                                    src={landingPageImage}
                                    alt="Landing Page Preview"
                                    className="w-full object-contain bg-gray-50"
                                  />
                                </ScrollArea>
                              </div>
                            ) : (
                              <div className="flex flex-col justify-center items-center h-[30svh] lg:h-[54svh] rounded-lg border-2 border-dashed border-border">
                                <div>
                                  <span>
                                    <Image className="w-16 h-16 text-border" strokeWidth={1.5} />
                                  </span>
                                </div>
                                <p className="text-sm text-themeforeground">Landing Page Screenshot will appear here</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <AccessControl
                      feature={CreativeOSFeatures.USE_CREATIVE_TOOLKIT}
                      FeatureUnlocked={
                        <div className="mt-4 flex justify-end gap-3">
                          <Button
                            type="button"
                            onClick={() => {
                              form.handleSubmit(handleSubmitUploadAdImage)()
                            }}
                            disabled={Boolean(
                              !form.watch('adImage') ||
                                !form.getValues('landingPageUrl') ||
                                isUploading ||
                                (landingPageImage && form.watch('landingPageUrl') === lastCapturedUrl),
                            )}
                          >
                            {isUploading ? 'Capturing...' : 'Capture'}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => {
                              form.handleSubmit(handleSubmitFunnelCongruenceAnalysis)()
                            }}
                            disabled={!adImageUrl || !landingPageImage || isUploading}
                          >
                            Next
                          </Button>
                        </div>
                      }
                      FeatureLocked={
                        <div className="mt-4 flex justify-end gap-3">
                          <Button disabled={true} size={'sm'}>
                            Analyze
                            <div>{<LockTagSmall />}</div>
                          </Button>
                        </div>
                      }
                    />
                  </form>
                )}
              </div>
            ) : uploadStep === 2 && funnelCongruenceAnalysis ? (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  <div className="lg:col-span-2 space-y-6 rounded-lg p-6 border ">
                    <h3 className="font-semibold text-lg">Audience Details</h3>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm">Ad Preview</h3>
                      <div>
                        <img
                          src={funnelCongruenceAnalysis.ad_images_data[0]}
                          alt=""
                          className="w-full h-[30svh] lg:h-[50svh] object-contain"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h3 className="font-semibold text-sm">Landing Page Preview</h3>
                      <ScrollArea className="h-[30svh] lg:h-[50svh]">
                        <img
                          src={funnelCongruenceAnalysis.landing_page_image_data}
                          alt={form.getValues('landingPageUrl')}
                        />
                      </ScrollArea>
                    </div>
                  </div>
                  <div className="lg:col-span-3 rounded-lg border">
                    <div>
                      <UnderlineTabs defaultValue="score">
                        <UnderlineTabsList>
                          <UnderlineTabsTrigger value="score">Score</UnderlineTabsTrigger>
                          <UnderlineTabsTrigger value="analysis">TL;DR</UnderlineTabsTrigger>
                          <UnderlineTabsTrigger value="fixes">Recommendations</UnderlineTabsTrigger>
                        </UnderlineTabsList>
                        <UnderlineTabsContent value="score">
                          <div className="p-6 ">
                            <div className="p-6 flex flex-col justify-center items-center">
                              <div className={'py-6 relative'}>
                                <SemiCircleProgressBar
                                  strokeWidth={6}
                                  percentage={
                                    funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score.score
                                  }
                                  diameter={width > 750 ? 300 : undefined}
                                  stroke={
                                    funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score.score >= 80
                                      ? '#28BE53BE'
                                      : funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score.score > 40
                                        ? '#FF8731'
                                        : '#FF0000'
                                  }
                                />
                                <div className={'absolute bottom-5 left-1/2 transform -translate-x-1/2 text-center'}>
                                  <h3 className={'text-3xl font-bold mb-2'}></h3>
                                  <h4 className={'text-4xl font-bold mb-2'}>
                                    {funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score.score}
                                  </h4>
                                  <div className={'font-medium flex items-center gap-2'}>
                                    <span>
                                      {funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score.grade}
                                    </span>
                                    <span className="text-thememutedforeground text-sm">&#8226;</span>{' '}
                                    <span>
                                      {
                                        funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score
                                          .emotional_label
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className={'mt-6 space-y-6 flex flex-col items-center text-center'}>
                                <p className={'font-light'}>
                                  {
                                    funnelCongruenceAnalysis.analysis.coherence_evaluation.overall_score
                                      .visceral_description
                                  }
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-3">
                              {Object.entries(
                                funnelCongruenceAnalysis.analysis.coherence_evaluation.score_breakdown,
                              ).map(([key, value]) => (
                                <ScoreMeasures
                                  key={key}
                                  score={value.score}
                                  name={key}
                                  description={value.description}
                                />
                              ))}
                            </div>
                          </div>
                        </UnderlineTabsContent>
                        <UnderlineTabsContent value="analysis">
                          <div className="p-6">
                            <div className="flex flex-col space-y-3">
                              <h5 className="font-semibold">Overview</h5>
                              <p className="text-sm font-normal text-themeforeground">
                                {funnelCongruenceAnalysis.analysis.analysis.overview}
                              </p>
                              <h5 className="font-semibold">What's Working Well</h5>
                              <p className="text-sm font-normal text-themeforeground">
                                {funnelCongruenceAnalysis.analysis.analysis.whatsWorkingWell}
                              </p>
                              <h5 className="font-semibold">Areas for Improvement</h5>
                              <p className="text-sm font-normal">
                                {funnelCongruenceAnalysis.analysis.analysis.areasForImprovement}
                              </p>
                            </div>
                          </div>
                        </UnderlineTabsContent>
                        <UnderlineTabsContent value="fixes">
                          <div className="p-6">
                            <div className="flex flex-col space-y-3">
                              {funnelCongruenceAnalysis.analysis.opportunity_assessment.key_opportunities.map(
                                (item, index) => (
                                  <FixesItem
                                    key={item.heading}
                                    heading={item.heading}
                                    description={item.description}
                                    index={index}
                                  />
                                ),
                              )}
                            </div>
                          </div>
                        </UnderlineTabsContent>
                      </UnderlineTabs>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </Form>
        </div>
      </Stack>
    </AccessControl>
  )
}
